import { useState, useEffect } from "react";
import { Boxes, Loader2, FileText, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { Modal } from "./SharedComponents";
import { generateRetiroStockPDF } from "./helpers";
import { CeldaAsignacionStock, asignacionValida } from "./AsignacionStock";   // Fix EV

/* Fix EJ (2026-09-27): "permitir en Análisis de Presupuestos la posibilidad de verificar el
   stock y asignar pedidos al inventario como está en Pedidos a Proveedores. Esto ayuda en
   los casos en los cuales llega un ingreso al inventario de un material solicitado durante
   el proceso de análisis" (CEN). Igual que Generar PP: cada ítem sigue a proveedor o sale
   del stock interno (material o herramienta). Lo que sale del stock se descuenta del
   análisis y genera la Solicitud de Retiro de Stock Interno (RSI), que se aprueba en
   Inventario → Pedido de Movimiento como siempre. */
export function AsignarStockAPModal({ act, items, cerrados, open, onClose, onGuardarAntes, onListo }) {
  const [filas, setFilas] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [tools, setTools] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [rsi, setRsi] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFilas((items || []).map(() => ({ origen: "proveedor", asign: [] })));   // Fix EV: uno o varios materiales por ítem
    setRsi(null);
    setCargando(true);
    Promise.all([
      axios.get(`${API}/inventory/materials`, { withCredentials: true }),
      axios.get(`${API}/inventory/tools`, { withCredentials: true }),
    ]).then(([m, t]) => { setMaterials(m.data || []); setTools(t.data || []); })
      .catch(() => toast.error("No se pudo cargar el inventario"))
      .finally(() => setCargando(false));
  }, [open, items]);

  if (!open || !act) return null;
  // Fix EY (2026-10-05): lo adjudicado a un proveedor ya cerrado queda con ese proveedor; el
  // resto del ítem sí puede salir del stock.
  const enCerrados = (it) => (it.allocations || []).reduce((s, a) => s + ((cerrados || []).includes(a.provider_idx) ? Number(a.quantity) || 0 : 0), 0);
  const libre = (it) => Math.max(0, Math.round(((Number(it?.quantity) || 0) - enCerrados(it || {})) * 10000) / 10000);
  const set = (i, fila) => setFilas(prev => prev.map((f, k) => (k === i ? fila : f)));
  // Fix EV: una línea por cada material elegido (la misma línea del análisis puede salir de varios).
  const elegidas = filas.flatMap((f, i) => (f.origen === "proveedor" ? [] : (f.asign || []).map(a => ({ i, origen: f.origen, ref: a.ref, cantidad: Number(a.cantidad) || 0 }))));
  const listo = elegidas.length > 0 && filas.every((f, i) => asignacionValida(f, libre(items[i]), materials));

  const confirmar = async () => {
    setEnviando(true);
    try {
      await onGuardarAntes();
      const { data } = await axios.post(`${API}/purchases/budget-acts/${act._id}/asignar-stock`, {
        lineas: elegidas.map(f => ({ item_idx: f.i, quantity: Number(f.cantidad), origen: f.origen, origen_material_id: f.ref.id })),
      }, { withCredentials: true });
      setRsi(data.rsi);
      onListo(data.act);
      toast.success(`Solicitud de retiro de stock interno: ${data.rsi.number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo asignar al stock");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !enviando && onClose()} title={rsi ? `Stock asignado · ${act.number}` : `Verificar stock · ${act.number}`} extraWide>
      {rsi ? (
        <div className="space-y-4" data-testid="ap-stock-done">
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-800">
              Se generó la solicitud de retiro de stock interno <strong>{rsi.number}</strong> ({(rsi.items || []).length} ítem{(rsi.items || []).length !== 1 ? "s" : ""}).
              {act.cubierto_por_stock ? "Todo el pedido sale del stock interno: el análisis quedó cerrado." : "Las cantidades ya se descontaron del análisis."} Se aprueba en Inventario → Pedido de Movimiento.
            </p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => generateRetiroStockPDF(rsi)}
              className="flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold"
              data-testid="ap-stock-pdf-btn">
              <FileText size={15} /> Solicitud de retiro (PDF)
            </button>
            <button type="button" onClick={onClose}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold">
              Cerrar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Elegí qué ítems salen del stock interno, de qué material y cuánto (un ítem puede salir de varios materiales, por ejemplo un rollo de cada marca). Esa cantidad <strong>se descuenta del análisis</strong> (deja de cotizarse) y se genera la solicitud de retiro de stock interno; lo que no alcanza sigue en el análisis para comprar.
          </p>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="text-gray-500 uppercase text-[10px]">
                  <th className="py-2 px-2 text-left">Descripción</th>
                  <th className="py-2 px-2 text-left w-14">UM</th>
                  <th className="py-2 px-2 text-left w-16">Cant.</th>
                  <th className="py-2 px-2 text-left w-96">Origen · material y cantidad a stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(items || []).map((it, i) => {
                  const f = filas[i] || { origen: "proveedor", asign: [] };
                  return (
                    <tr key={i} className={f.origen !== "proveedor" ? "bg-amber-50/40" : ""} data-testid={`ap-stock-row-${i}`}>
                      <td className="py-2 px-2 font-medium text-gray-800 align-top">{it.item_name}</td>
                      <td className="py-2 px-2 text-gray-500 align-top">{it.unit}</td>
                      <td className="py-2 px-2 font-semibold text-gray-900 align-top">
                        {it.quantity}
                        {enCerrados(it) > 0 && libre(it) > 0 && (
                          <p className="text-[10px] font-normal text-gray-500 leading-tight" data-testid={`ap-stock-${i}-cerrado`}>{enCerrados(it)} con proveedor cerrado · quedan {libre(it)}</p>
                        )}
                      </td>
                      <td className="py-2 px-2 align-top">
                        {cargando && f.origen !== "proveedor" ? (
                          <p className="flex items-center gap-2 text-xs text-gray-400"><Loader2 size={14} className="animate-spin" /> Cargando inventario...</p>
                        ) : (
                          <CeldaAsignacionStock fila={f} total={libre(it)} unidad={it.unit} onChange={fila => set(i, fila)}
                            materials={materials} tools={tools} testId={`ap-stock-${i}`} cerrado={libre(it) <= 0} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={enviando}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">
              Cancelar
            </button>
            <button type="button" onClick={confirmar} disabled={enviando || !listo}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="ap-stock-confirmar">
              {enviando ? <Loader2 size={15} className="animate-spin" /> : <Boxes size={15} />}
              {enviando ? "Procesando..." : "Asignar al stock y generar solicitud"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
